"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const NCUKPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-manrope">
        {/* Hero Section Skeleton */}
        <section className="relative h-screen overflow-hidden">
          <div className="absolute inset-0 bg-gray-800 shimmer"></div>
          <div className="relative z-10 h-full flex items-center justify-center">
            <div className="text-center max-w-4xl mx-auto px-4">
              <div className="h-16 bg-gray-700 rounded mb-6 shimmer"></div>
              <div className="space-y-3">
                <div className="h-6 bg-gray-700 rounded shimmer"></div>
                <div className="h-6 bg-gray-700 rounded w-4/5 mx-auto shimmer"></div>
                <div className="h-6 bg-gray-700 rounded w-3/5 mx-auto shimmer"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Sections Skeleton */}
        <div className="space-y-16 py-16">
          {[1, 2, 3].map((i) => (
            <div key={i} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="h-64 bg-gray-800 rounded shimmer"></div>
                <div className="space-y-4">
                  <div className="h-8 bg-gray-700 rounded shimmer"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-700 rounded shimmer"></div>
                    <div className="h-4 bg-gray-700 rounded w-4/5 shimmer"></div>
                    <div className="h-4 bg-gray-700 rounded w-3/5 shimmer"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-manrope">
      {/* Hero Banner Section */}
      <section className="relative h-[45vh] sm:h-[60vh] md:h-[80vh] lg:h-screen overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://www.bi-bangkok.com/ncuk.jpg')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-end pb-4 sm:pb-8 md:pb-14 lg:pb-18">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-6xl xl:text-7xl font-bold font-lora mb-3 sm:mb-6 text-brand-beige">
                What is NCUK{" "}
              </h1>
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl text-white leading-relaxed">
                Your guaranteed pathway to 45+ leading universities worldwide.
                Over 40,000 successful student placements since 1987.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}

      {/* What is NCUK Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-8">
                <span className="inline-block px-4 py-2 bg-red-600/20 text-red-400 rounded-full text-sm font-medium mb-4">
                  About NCUK
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-lora mb-2 text-white">
                  What is NCUK?
                </h2>
                <div className="w-20 h-1 bg-red-500 mt-4"></div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 mr-4">
                    <svg
                      className="w-6 h-6 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    Northern Consortium of UK Universities (NCUK) is a group of
                    leading UK universities dedicated to providing international
                    students guaranteed access to universities worldwide.
                    Founded in 1987.
                  </p>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 mr-4">
                    <svg
                      className="w-6 h-6 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    A UK-recognized foundation and university year one
                    qualification accepted by 50+ global universities across the
                    UK, Australia, New Zealand, USA, Canada, and Europe.
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/30">
                  12 Member Universities
                </span>
                <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/30">
                  Founded in 1987
                </span>
                <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/30">
                  50+ Global Partners
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl p-8 h-full shadow-2xl border border-red-500/20">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
                    <svg
                      className="w-8 h-8 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-red-400 mb-2 font-lora">
                    Global Recognition
                  </h3>
                  <p className="text-gray-300">
                    NCUK qualifications open doors to universities worldwide
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">
                      12
                    </div>
                    <div className="text-white text-sm">
                      Member Universities
                    </div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">
                      50+
                    </div>
                    <div className="text-white text-sm">Global Partners</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">
                      35+
                    </div>
                    <div className="text-white text-sm">Years Experience</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">
                      6
                    </div>
                    <div className="text-white text-sm">Continents</div>
                  </div>
                </div>

                <div className="pt-4 border-t border-red-500/20">
                  <h4 className="text-lg font-semibold text-white mb-3">
                    Accepted in countries including:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-md text-sm border border-red-500/20">
                      United Kingdom
                    </span>
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-md text-sm border border-red-500/20">
                      Australia
                    </span>
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-md text-sm border border-red-500/20">
                      USA
                    </span>
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-md text-sm border border-red-500/20">
                      Canada
                    </span>
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-md text-sm border border-red-500/20">
                      New Zealand
                    </span>
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-md text-sm border border-red-500/20">
                      Europe
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Logo Visibility Section */}

      {/* Why do we work with NCUK Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-lora font-bold text-center mb-12 md:mb-20 text-brand-beige">
            Why do we work with NCUK?
          </h2>

          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="space-y-6 md:space-y-8">
              <p className="text-lg md:text-xl leading-relaxed text-gray-200">
                <span className="text-brand-red font-semibold text-xl md:text-2xl">
                  Northern Consortium of UK Universities (NCUK)
                </span>
                , founded in 1987, is a consortium of leading UK universities
                dedicated to providing international students with guaranteed
                pathways to top universities worldwide.
              </p>

              <div className="bg-gradient-to-r from-black to-gray-900 p-6 md:p-8 border-l-4 border-brand-red shadow-xl rounded-lg">
                <h3 className="text-2xl md:text-3xl font-lora font-bold text-brand-beige mb-4 md:mb-6">
                  Why do we work with NCUK?
                </h3>
                <ul className="space-y-3 md:space-y-4 text-base md:text-lg">
                  <li className="flex items-center group">
                    <div className="w-3 h-3 bg-brand-red rounded-full mr-4 group-hover:scale-125 transition-transform duration-300 flex-shrink-0"></div>
                    <span className="text-brand-red font-semibold group-hover:text-brand-beige transition-colors duration-300">
                      Guaranteed university placement for successful students
                    </span>
                  </li>
                  <li className="flex items-center group">
                    <div className="w-3 h-3 bg-brand-red rounded-full mr-4 group-hover:scale-125 transition-transform duration-300 flex-shrink-0"></div>
                    <span className="text-brand-red font-semibold group-hover:text-brand-beige transition-colors duration-300">
                      30+ years of proven success with 40,000+ students
                    </span>
                  </li>
                  <li className="flex items-center group">
                    <div className="w-3 h-3 bg-brand-red rounded-full mr-4 group-hover:scale-125 transition-transform duration-300 flex-shrink-0"></div>
                    <span className="text-brand-red font-semibold group-hover:text-brand-beige transition-colors duration-300">
                      Pathway to 10 universities in top 100 QS World Rankings
                    </span>
                  </li>
                  <li className="flex items-center group">
                    <div className="w-3 h-3 bg-brand-red rounded-full mr-4 group-hover:scale-125 transition-transform duration-300 flex-shrink-0"></div>
                    <span className="text-brand-red font-semibold group-hover:text-brand-beige transition-colors duration-300">
                      Global recognition across 45+ partner universities
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center mt-8 lg:mt-0">
  <div className="bg-white shadow-2xl hover:shadow-brand-red/20 transition-all duration-500 transform hover:scale-105 max-w-sm w-full rounded-xl overflow-hidden">
    <img
      src="https://bic-bangkok.vercel.app/ncukone.jpg"
      alt="NCUK Logo"
      className="w-full h-full object-cover"
    />
  </div>
</div>
          </div>
        </div>
      </section>

      {/* New Section: Benefits of NCUK */}
      <section className="py-16 md:py-20 lg:py-24 bg-dark-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              Key Benefits of NCUK Program
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "🎓",
                title: "Guaranteed Placement",
                description:
                  "Successful students receive guaranteed admission to one of our 45+ partner universities",
              },
              {
                icon: "🌍",
                title: "Global Recognition",
                description:
                  "Qualifications recognized worldwide in UK, Australia, NZ, USA, Canada, and Europe",
              },
              {
                icon: "⚡",
                title: "Fast Track",
                description:
                  "Complete your foundation year and progress directly to undergraduate studies",
              },
              {
                icon: "👨‍🏫",
                title: "Expert Teaching",
                description:
                  "Learn from experienced educators with international teaching expertise",
              },
              {
                icon: "📚",
                title: "Comprehensive Curriculum",
                description:
                  "Designed specifically to prepare international students for university success",
              },
              {
                icon: "🤝",
                title: "Personal Support",
                description:
                  "Dedicated student support services including visa and accommodation assistance",
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className="text-2xl font-bold text-brand-red mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Universities Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              Founding Universities of NCUK
            </h2>
          </div>

          {/* University of Manchester */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-16">
            <div className="space-y-6 md:space-y-8 order-2 lg:order-1">
              <h3 className="text-2xl md:text-3xl font-bold text-brand-red">
                University of Manchester
              </h3>
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                A world-renowned Russell Group university consistently ranked
                among the top universities globally. Known for excellence in
                research and teaching across diverse disciplines.
              </p>
            </div>
            <div className="flex justify-center order-1 lg:order-2">
              <div className="bg-white p-6 md:p-8 shadow-2xl hover:shadow-brand-red/20 transition-all duration-500 transform hover:scale-105 max-w-xs w-full rounded-xl">
                <img
                  src="https://bic-bangkok.vercel.app/manchester.png"
                  alt="University of Manchester Logo"
                  className="max-w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* University of Sheffield */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-16">
            <div className="flex justify-center">
              <div className="bg-white p-6 md:p-8 shadow-2xl hover:shadow-brand-red/20 transition-all duration-500 transform hover:scale-105 max-w-xs w-full rounded-xl">
                <img
                  src="https://bic-bangkok.vercel.app/sheffield.svg"
                  alt="University of Sheffield Logo"
                  className="max-w-full h-auto rounded-lg"
                />
              </div>
            </div>
            <div className="space-y-6 md:space-y-8">
              <h3 className="text-2xl md:text-3xl font-bold text-brand-red">
                University of Sheffield
              </h3>
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                A leading Russell Group university famous for its engineering
                and research excellence. Consistently ranked among the top
                universities in the UK and worldwide.
              </p>
            </div>
          </div>

          {/* University of Liverpool */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-16">
            <div className="space-y-6 md:space-y-8 order-2 lg:order-1">
              <h3 className="text-2xl md:text-3xl font-bold text-brand-red">
                University of Liverpool
              </h3>
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                A prestigious Russell Group university with a rich history of
                innovation and academic excellence. Known for its world-leading
                research in health sciences, engineering, and humanities, and
                for being the alma mater of several Nobel laureates.
              </p>
            </div>
            <div className="flex justify-center order-1 lg:order-2">
              <div className="bg-white p-6 md:p-8 shadow-2xl hover:shadow-brand-red/20 transition-all duration-500 transform hover:scale-105 max-w-xs w-full rounded-xl">
                <img
                  src="https://bic-bangkok.vercel.app/liverpool.png"
                  alt="University of Liverpool Logo"
                  className="max-w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* University of Leeds */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="flex justify-center">
              <div className="bg-white p-6 md:p-8 shadow-2xl hover:shadow-brand-red/20 transition-all duration-500 transform hover:scale-105 max-w-xs w-full rounded-xl">
                <img
                  src="https://bic-bangkok.vercel.app/leeds.png"
                  alt="University of Leeds Logo"
                  className="max-w-full h-auto rounded-lg"
                />
              </div>
            </div>
            <div className="space-y-6 md:space-y-8">
              <h3 className="text-2xl md:text-3xl font-bold text-brand-red">
                University of Leeds
              </h3>
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                A prestigious Russell Group university with a global reputation
                for teaching and research. Known for its vibrant campus and
                strong industry connections.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* JOIN US Section (Similar to GED page) */}
      <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-dark-blue to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              JOIN US TODAY
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Become part of our successful NCUK community and start your
              journey to world-class education
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Apply Online",
                description:
                  "Submit your application through our streamlined online portal",
                icon: "📝",
              },
              {
                step: "02",
                title: "Get Assessment",
                description:
                  "Receive personalized guidance and academic assessment",
                icon: "🎯",
              },
              {
                step: "03",
                title: "Start Learning",
                description:
                  "Begin your NCUK program with expert instructors and support",
                icon: "🚀",
              },
            ].map((step, index) => (
              <div
                key={index}
                className="relative bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-brand-red rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-brand-beige font-bold text-xl">
                    {step.step}
                  </span>
                </div>
                <div className="text-4xl mb-4 mt-4">{step.icon}</div>
                <h3 className="text-2xl font-bold text-brand-beige mb-4 group-hover:text-brand-red transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate("/enquire")}
              className="bg-brand-red hover:bg-red-700 text-white font-bold py-4 px-12 text-lg transition-all duration-300 hover:transform hover:scale-105 shadow-lg hover:shadow-xl rounded-lg"
            >
              START YOUR APPLICATION
            </button>
          </div>
        </div>
      </section>

      {/* New Section: Student Success Stories */}

      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-lora font-bold text-center text-brand-red mb-12 md:mb-20">
            Entry Requirements
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            <div className="bg-dark-blue p-6 sm:p-8 md:p-10 text-center border-2 border-brand-beige/30 hover:border-brand-beige transition-all duration-500 transform hover:-translate-y-4 hover:shadow-2xl hover:shadow-brand-beige/30 group">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-brand-beige flex items-center justify-center mx-auto mb-6 md:mb-8 group-hover:scale-110 transition-transform duration-500">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-brand-beige mb-4 md:mb-6 group-hover:text-white transition-colors duration-300">
                Age
              </h3>
              <p className="text-white text-lg md:text-xl leading-relaxed">
                Applicants must be at least{" "}
                <span className="text-brand-beige font-bold text-2xl md:text-3xl bg-brand-beige/10 px-2 py-1">
                  16 years old
                </span>{" "}
                to be eligible for enrollment.
              </p>
            </div>

            <div className="bg-dark-blue p-6 sm:p-8 md:p-10 text-center border-2 border-brand-beige/30 hover:border-brand-beige transition-all duration-500 transform hover:-translate-y-4 hover:shadow-2xl hover:shadow-brand-beige/30 group">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-brand-beige flex items-center justify-center mx-auto mb-6 md:mb-8 group-hover:scale-110 transition-transform duration-500">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13m6-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-brand-beige mb-4 md:mb-6 group-hover:text-white transition-colors duration-300">
                English Level
              </h3>
              <p className="text-white text-lg md:text-xl leading-relaxed">
                English Proficiency Level{" "}
                <span className="text-brand-beige font-bold text-2xl md:text-3xl bg-brand-beige/10 px-2 py-1">
                  Intermediate
                </span>
              </p>
            </div>

            <div className="bg-dark-blue p-6 sm:p-8 md:p-10 text-center border-2 border-brand-beige/30 hover:border-brand-beige transition-all duration-500 transform hover:-translate-y-4 hover:shadow-2xl hover:shadow-brand-beige/30 group sm:col-span-2 lg:col-span-1">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-brand-beige flex items-center justify-center mx-auto mb-6 md:mb-8 group-hover:scale-110 transition-transform duration-500">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-brand-beige mb-4 md:mb-6 group-hover:text-white transition-colors duration-300">
                Academic
              </h3>
              <p className="text-white text-lg md:text-xl leading-relaxed">
                Must pass the{" "}
                <span className="text-brand-beige font-bold text-lg md:text-xl bg-brand-beige/10 px-2 py-1">
                  entry test
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* University Requirements Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-dark-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              Guaranteed Admission Requirements
            </h2>
            <h3 className="text-2xl md:text-3xl font-bold text-brand-red mb-4">
              Top UK Universities
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Requirements for guaranteed admission with NCUK International
              Foundation Year
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* University of Manchester */}
            <div className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/manchester.png"
                  alt="University of Manchester"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Manchester
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BSc (Hons) Business Accounting with Industrial/Professional
                    Experience
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">AAA</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">A</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* University of Sheffield */}
            <div className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/sheffield.svg"
                  alt="University of Sheffield"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Sheffield
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BEng (Hons) Civil Engineering
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">AAA</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">B</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* University of Leeds */}
            <div className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/leeds.png"
                  alt="University of Leeds"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Leeds
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BEng (Hons) Civil and Environmental Engineering
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">AAA</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New Zealand Universities Requirements */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-brand-red mb-4">
              New Zealand Universities
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Requirements for guaranteed admission with NCUK International
              Foundation Year
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* University of Auckland - Program 1 */}
            <div className="bg-dark-blue rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src=".https://bic-bangkok.vercel.app/auckland.png"
                  alt="University of Auckland"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Auckland
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BE (Hons) Biomedical Engineering
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">ABB</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">B</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* University of Auckland - Program 2 */}
            <div className="bg-dark-blue rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/auckland.png"
                  alt="University of Auckland"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Auckland
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    Bachelor of Global Studies International Relations and
                    Business
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">
                        BBC-ABB
                      </span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">B</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* University of Auckland - Program 3 */}
            <div className="bg-dark-blue rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/auckland.png"
                  alt="University of Auckland"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Auckland
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    Bachelor of Commerce
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">BCC</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Australian Universities Requirements */}
      <section className="py-16 md:py-20 lg:py-24 bg-dark-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-brand-red mb-4">
              Australian Universities
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Requirements for guaranteed admission with NCUK International
              Foundation Year
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* University of Western Australia */}
            <div className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/waus.png"
                  alt="University of Western Australia"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  University of Western Australia
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BSc (Hons) Business Accounting
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">BBC</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">B</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RMIT University */}
            <div className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/rmit.png"
                  alt="RMIT University"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  RMIT University
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BSc (Hons) Business Accounting
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">BCC</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">B</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Swinburne University of Technology */}
            <div className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center mb-6">
                <img
                  src="https://bic-bangkok.vercel.app/swin.png"
                  alt="Swinburne University of Technology"
                  className="w-16 h-16 mr-4 object-contain bg-white p-2 rounded-lg"
                />
                <h4 className="text-xl font-bold text-brand-beige">
                  Swinburne University of Technology
                </h4>
              </div>
              <div className="space-y-4">
                <div className="border-l-4 border-brand-red pl-4">
                  <p className="text-white font-semibold text-lg">
                    BSc (Hons) Business Accounting
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        IFY Grades:
                      </span>
                      <span className="text-white font-bold text-xl">CCC</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900 px-4 py-3 rounded-lg">
                      <span className="text-brand-beige font-semibold">
                        EAP Requirements:
                      </span>
                      <span className="text-white font-bold text-xl">C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Details Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              Program Details
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                number: "01",
                title: "Duration",
                description: "9-12 months intensive preparation",
              },
              {
                number: "02",
                title: "Intakes",
                description: "Multiple intakes throughout the year",
              },
              {
                number: "03",
                title: "Assessment",
                description: "Combination of exams and coursework",
              },
              {
                number: "04",
                title: "Pathways",
                description: "Direct entry to Year 1 undergraduate",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="relative bg-dark-blue rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="absolute -left-4 top-8 w-20 h-16 bg-brand-red rounded-r-full flex items-center justify-center shadow-lg">
                  <span className="text-brand-beige font-bold text-2xl ml-2">
                    {item.number}
                  </span>
                </div>
                <div className="ml-12">
                  <h3 className="text-2xl font-bold text-brand-beige mb-4 group-hover:text-brand-red transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Apply CTA */}
      <section className="relative py-20 md:py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')`,
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-lora mb-8 text-brand-beige">
            Ready to Start Your NCUK Journey?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-12 max-w-4xl mx-auto leading-relaxed">
            Take the first step toward guaranteed university placement. Join
            Bridge International and unlock your pathway to the world's top
            universities.
          </p>

          <button
            onClick={() => navigate("/enquire")}
            className="bg-brand-red hover:bg-red-700 text-white font-bold py-4 px-12 text-lg transition-all duration-300 hover:transform hover:scale-105 shadow-lg hover:shadow-xl rounded-lg"
          >
            ENROLL NOW
          </button>
        </div>
      </section>
    </div>
  );
};

export default NCUKPage;
